import { useDisclosure } from '@heroui/react';
import { toast } from 'react-toastify';
import { useState, useCallback } from 'react';
import { useAtom } from 'jotai';

import { createLeadNote, deleteLeadNote } from '@/store';
import type { Lead, LeadNote } from '@/types';

export function useLeadNotes() {
  const [, createNote] = useAtom(createLeadNote);
  const [, deleteNote] = useAtom(deleteLeadNote);
  const { isOpen: isAddModalOpen, onOpen: onAddModalOpen, onOpenChange: onAddModalOpenChange } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onOpenChange: onDeleteModalOpenChange } = useDisclosure();
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<LeadNote | null>(null);

  const openAddModal = useCallback(
    (lead: Lead) => {
      setSelectedLead(lead);
      onAddModalOpen();
    },
    [onAddModalOpen]
  );

  const handleCreateNote = useCallback(
    async (note: string) => {
      if (!selectedLead) return;

      try {
        setIsSubmitting(true);
        await createNote({ leadId: selectedLead.id, note });
        toast.success('Note added successfully');
        setIsSubmitting(false);
        onAddModalOpenChange();
        setSelectedLead(null);
      } catch (error) {
        setIsSubmitting(false);
        // Error is handled by axios interceptor
      }
    },
    [selectedLead, onAddModalOpenChange, createNote]
  );

  const openDeleteModal = useCallback(
    (lead: Lead, note: LeadNote) => {
      setSelectedLead(lead);
      setNoteToDelete(note);
      onDeleteModalOpen();
    },
    [onDeleteModalOpen]
  );

  const handleDeleteNote = useCallback(async () => {
    if (!selectedLead || !noteToDelete) return;

    try {
      setIsDeleting(true);
      await deleteNote({ leadId: selectedLead.id, noteId: noteToDelete.id });
      toast.success('Note deleted successfully');
      setIsDeleting(false);
      setSelectedLead(null);
      setNoteToDelete(null);
      onDeleteModalOpenChange();
    } catch (error) {
      setIsDeleting(false);
    }
  }, [selectedLead, noteToDelete, deleteNote, onDeleteModalOpenChange]);

  return {
    isAddModalOpen,
    selectedLead,
    isSubmitting,
    isDeleting,
    openAddModal,
    handleCreateNote,
    handleDeleteNote,
    isDeleteModalOpen,
    openDeleteModal,
    noteToDelete,
    onAddModalOpenChange,
    onDeleteModalOpenChange
  };
}

